import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { FaQuestionCircle, FaEdit, FaUpload, FaTimes } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import { listingService } from '../services/listingService';
import { locationData, provinces } from '../utils/locationData';
import ConfirmDialog from '../components/ConfirmDialog';

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
  background-color: ${props => props.theme.colors.background};
  min-height: calc(100vh - 200px);
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const FormCard = styled.div`
  background-color: ${props => props.theme.colors.surface};
  border-radius: 10px;
  padding: 2rem;
  box-shadow: 0 2px 10px ${props => props.theme.colors.shadow};
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const Title = styled.h1`
  color: ${props => props.theme.colors.primary};
  text-align: center;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
    margin-bottom: 1.5rem;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  font-weight: 500;
  margin-bottom: 0.5rem;
  color: ${props => props.theme.colors.text};
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 5px;
  font-size: 1rem;
  background-color: ${props => props.theme.colors.surface};
  color: ${props => props.theme.colors.text};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
  
  &::placeholder {
    color: ${props => props.theme.colors.textSecondary};
  }
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
    padding: 0.65rem;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 5px;
  font-size: 1rem;
  background-color: ${props => props.theme.colors.surface};
  color: ${props => props.theme.colors.text};
  min-height: 120px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
  
  &::placeholder {
    color: ${props => props.theme.colors.textSecondary};
  }
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
    padding: 0.65rem;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 5px;
  font-size: 1rem;
  background-color: ${props => props.theme.colors.surface};
  color: ${props => props.theme.colors.text};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
    padding: 0.65rem;
  }
`;

const MultiSelect = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const SkillTag = styled.span`
  background: ${props => props.selected ? props.theme.colors.primary : props.theme.colors.background};
  color: ${props => props.selected ? 'white' : props.theme.colors.text};
  padding: 0.5rem 1rem;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.9rem;
  border: 1px solid ${props => props.selected ? 'transparent' : props.theme.colors.border};
  
  &:hover {
    background: ${props => props.theme.colors.primary};
    color: white;
    border-color: transparent;
  }
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
    padding: 0.4rem 0.75rem;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: 2rem;
`;

const FAQButton = styled.button`
  background: none;
  border: 2px solid ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.primary};
  padding: 0.75rem 1.5rem;
  border-radius: 25px;
  cursor: pointer;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.theme.colors.primary};
    color: white;
  }
`;

const PostButton = styled.button`
  background: linear-gradient(135deg, #8B5A96, #A569BD);
  color: white;
  border: none;
  padding: 0.75rem 2rem;
  border-radius: 25px;
  cursor: pointer;
  font-weight: bold;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0,0,0,0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: ${props => props.theme.colors.surface};
  padding: 2rem;
  border-radius: 10px;
  max-width: 600px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
`;

const FAQItem = styled.div`
  margin-bottom: 1.5rem;
`;

const FAQQuestion = styled.h4`
  color: ${props => props.theme.colors.primary};
  margin-bottom: 0.5rem;
`;

const FAQAnswer = styled.p`
  color: ${props => props.theme.colors.textSecondary};
  line-height: 1.5;
`;

const CloseButton = styled.button`
  background: ${props => props.theme.colors.primary};
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 5px;
  cursor: pointer;
  float: right;
  margin-top: 1rem;
`;

const LogoUpload = styled.div`
  width: 100px;
  height: 100px;
  border: 2px dashed #ddd;
  border-radius: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  margin-bottom: 1rem;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: #8B5A96;
    background-color: #f8f9fa;
  }
`;

const LoginPrompt = styled.div`
  background-color: #fff3cd;
  color: #856404;
  padding: 2rem;
  border-radius: 10px;
  text-align: center;
  margin-bottom: 2rem;
`;

const LoginButton = styled.button`
  background: linear-gradient(135deg, #8B5A96, #A569BD);
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 20px;
  cursor: pointer;
  margin-left: 1rem;
`;

const skills = [
  "Agriculture & Farming", "Communication & Public Relations", "Construction & Trades",
  "Customer Service & Support", "Data & Analytics", "Design & Creative Arts",
  "Education & Training", "Engineering & Technical", "Environmental & Sustainability",
  "Finance & Accounting", "Healthcare & Medical", "Hospitality & Tourism",
  "Human Resources & People Management", "Legal & Compliance", "Logistics & Supply Chain",
  "Manufacturing & Production", "Marketing & Sales", "Media & Entertainment",
  "Personal Services & Lifestyle", "Project Management", "Science & Research",
  "Sports & Fitness", "Technology & IT", "Writing & Content Creation"
];

const industries = [
  "Agriculture & Forestry", "Mining & Natural Resources", "Manufacturing",
  "Construction & Real Estate", "Utilities & Energy", "Transportation & Logistics",
  "Retail & Wholesale Trade", "Hospitality & Tourism", "Financial Services",
  "Healthcare & Life Sciences", "Education & Training", "Information & Communication Technology (ICT)",
  "Media & Entertainment", "Professional & Business Services", "Public Sector & Non-Profit",
  "Arts, Culture & Creative Industries", "Sports & Recreation", "Environmental & Sustainability",
  "Aerospace & Defense", "Consumer Services"
];

const ShareHustle = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, profile } = useAuth();
  const [showFAQ, setShowFAQ] = useState(false);
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const editListing = location.state?.editListing;
  const isEditing = !!editListing;
  
  const [formData, setFormData] = useState({
    title: '',
    shortDescription: '',
    location: { province: '', area: '', customArea: '' },
    traveling: false,
    fullStory: '',
    age: '',
    size: '',
    industry: '',
    role: '',
    timeCommitment: '',
    pay: '',
    remote: ''
  });
  
  const [availableAreas, setAvailableAreas] = useState([]);

  // Load edit data if editing
  useEffect(() => {
    if (editListing) {
      const location = editListing.location || { province: '', area: '', customArea: '' };
      setFormData({
        title: editListing.title || '',
        shortDescription: editListing.short_description || '',
        location: location,
        traveling: editListing.traveling || false,
        fullStory: editListing.full_story || '',
        age: editListing.age || '',
        size: editListing.size || '',
        industry: editListing.industry || '',
        role: editListing.role || '',
        timeCommitment: editListing.time_commitment || '',
        pay: editListing.pay || '',
        remote: editListing.remote || ''
      });
      setSelectedSkills(editListing.skills || []);
      // Set available areas if province is set
      if (location.province) {
        setAvailableAreas(locationData[location.province] || []);
      }
    }
  }, [editListing]);

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      
      // Handle province change - reset area and update available areas
      if (field === 'location.province') {
        setFormData(prev => ({
          ...prev,
          location: { ...prev.location, province: value, area: '', customArea: '' }
        }));
        setAvailableAreas(value && value !== 'Anywhere' ? (locationData[value] || []) : []);
      } 
      // Handle area change - clear custom area if not "Other"
      else if (field === 'location.area') {
        setFormData(prev => ({
          ...prev,
          location: { ...prev.location, area: value, customArea: value !== 'Other' ? '' : prev.location.customArea }
        }));
      }
      else {
        setFormData(prev => ({
          ...prev,
          [parent]: { ...prev[parent], [child]: value }
        }));
      }
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleSkillToggle = (skill) => {
    setSelectedSkills(prev => 
      prev.includes(skill) 
        ? prev.filter(s => s !== skill)
        : [...prev, skill]
    );
  };

  const handlePost = async () => {
    // Validation
    if (!formData.title || !formData.shortDescription || !formData.fullStory || selectedSkills.length === 0) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!user || !profile) {
      toast.error('You must be logged in to create a post');
      return;
    }

    setShowConfirmDialog(true);
  };

  const handleConfirm = async () => {
    setShowConfirmDialog(false);
    setLoading(true);

    try {
      const listingData = {
        type: 'hustle',
        title: formData.title,
        short_description: formData.shortDescription,
        description: formData.shortDescription,
        full_story: formData.fullStory,
        location: formData.location,
        traveling: formData.traveling,
        remote: formData.remote,
        age: formData.age,
        size: formData.size,
        industry: formData.industry,
        role: formData.role,
        skills: selectedSkills,
        time_commitment: formData.timeCommitment,
        pay: formData.pay
      };

      if (isEditing) {
        // Update existing listing and send back to moderation
        await listingService.updateListing(editListing.id, {
          ...listingData,
          status: 'pending_moderation',
          submitted_at: new Date().toISOString()
        });
        toast.success(
          'Hustle updated successfully! Your changes have been submitted for review. ' +
          'Our team will validate and approve your post within 2 business days.'
        );
        navigate('/profile');
      } else {
        // Create new listing
        listingData.owner_id = user.id;
        listingData.status = 'draft';
        
        const createdListing = await listingService.createListing(listingData);
        await listingService.submitForModeration(createdListing.id);

        toast.success(
          'Congratulations! Your post has been submitted for review. ' +
          'Our team will validate and approve your post within 2 business days. ' +
          'You will receive a notification once it is approved.'
        );
        
        navigate('/find-hustle');
      }
    } catch (error) {
      console.error(`Error ${isEditing ? 'updating' : 'creating'} listing:`, error);
      toast.error(`Failed to ${isEditing ? 'update' : 'create'} listing. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  const faqItems = [
    {
      question: "What do I include in the full story?",
      answer: "This is where you could tell the story of how the venture started and the vision you have for where it's going. An important part to include is what you think you need to get the venture moving forward. This will be the bulk of the information people see when seeing your post."
    },
    {
      question: "Can I include personal details in my post?",
      answer: "Due to the POPIA act, you cannot include any personal or unique business information in your post. Unique information includes Contact details, Business names, addresses, bank details or social details."
    },
    {
      question: "What must I put in the description if I can't include the business title?",
      answer: "You can include a brief description of what the business does. Think of it like a category. For example, 'Home-made organic stuffed cookies'"
    },
    {
      question: "What if I don't know what skill I need to grow my venture?",
      answer: "Select the closest skill you think may match what your venture needs or a skill you think you don't have but the venture would benefit from."
    }
  ];

  return (
    <Container>
      <FormCard>
        <Title>{isEditing ? 'Edit your Hustle' : 'Share your Hustle'}</Title>
        <p style={{ textAlign: 'center', color: '#666', marginBottom: '2rem' }}>
          {isEditing 
            ? 'Update your hustle details below'
            : 'Tell your story, describe your venture, and outline the skills you\'re seeking to help your business grow'
          }
        </p>

        <FormGroup>
          <Label>Venture Title (do not include business name)</Label>
          <Input
            placeholder="Give your venture a catchy title..."
            value={formData.title}
            onChange={(e) => handleInputChange('title', e.target.value)}
          />
        </FormGroup>

        <FormGroup>
          <Label>Short business description</Label>
          <Input
            placeholder="One line summary of your venture..."
            value={formData.shortDescription}
            onChange={(e) => handleInputChange('shortDescription', e.target.value)}
          />
        </FormGroup>

        <FormGroup>
          <Label>Location</Label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
            <Select
              value={formData.location.province}
              onChange={(e) => handleInputChange('location.province', e.target.value)}
            >
              <option value="">Select Province</option>
              <option value="Anywhere">Anywhere (Remote)</option>
              {provinces.map(province => (
                <option key={province} value={province}>{province}</option>
              ))}
            </Select>
            <Select
              value={formData.location.area}
              onChange={(e) => handleInputChange('location.area', e.target.value)}
              disabled={!formData.location.province || formData.location.province === 'Anywhere'}
            >
              <option value="">Select Area</option>
              {availableAreas.map(area => (
                <option key={area} value={area}>{area}</option>
              ))}
              <option value="Other">Other</option>
            </Select>
          </div>
          {formData.location.area === 'Other' && (
            <Input
              placeholder="Enter your area/city"
              value={formData.location.customArea}
              onChange={(e) => handleInputChange('location.customArea', e.target.value)}
              style={{ marginTop: '0.5rem' }}
            />
          )}
        </FormGroup>

        <FormGroup>
          <Label>Traveling</Label>
          <Select
            value={formData.traveling}
            onChange={(e) => handleInputChange('traveling', e.target.value)}
          >
            <option value="">Select...</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </Select>
        </FormGroup>

        <FormGroup>
          <Label>Full story (Here you can give the full story of your business. How it started. How it's going. What you need from someone. How you think it will grow.)</Label>
          <TextArea
            style={{ minHeight: '150px' }}
            placeholder="Tell the full story of your venture..."
            value={formData.fullStory}
            onChange={(e) => handleInputChange('fullStory', e.target.value)}
          />
        </FormGroup>

        <FormGroup>
          <Label>Age</Label>
          <Select
            value={formData.age}
            onChange={(e) => handleInputChange('age', e.target.value)}
          >
            <option value="">Select business age...</option>
            <option value="0-1">0-1 years</option>
            <option value="1-2">1-2 years</option>
            <option value="2-3">2-3 years</option>
            <option value="3-5">3-5 years</option>
            <option value="5+">5+ years</option>
          </Select>
        </FormGroup>

        <FormGroup>
          <Label>Size</Label>
          <Select
            value={formData.size}
            onChange={(e) => handleInputChange('size', e.target.value)}
          >
            <option value="">Select team size...</option>
            <option value="1">1 person</option>
            <option value="2">2 people</option>
            <option value="3-5">3-5 people</option>
            <option value="5-10">5-10 people</option>
            <option value="10+">10+ people</option>
          </Select>
        </FormGroup>

        <FormGroup>
          <Label>Industry</Label>
          <Select
            value={formData.industry}
            onChange={(e) => handleInputChange('industry', e.target.value)}
          >
            <option value="">Select industry...</option>
            {industries.map(industry => (
              <option key={industry} value={industry}>{industry}</option>
            ))}
          </Select>
        </FormGroup>

        <FormGroup>
          <Label>Role</Label>
          <Select
            value={formData.role}
            onChange={(e) => handleInputChange('role', e.target.value)}
          >
            <option value="">Select role type...</option>
            <option value="partner">Partner</option>
            <option value="employee">Full-time Employee</option>
            <option value="part-time">Part-time</option>
            <option value="contract">Contract Basis</option>
            <option value="few-hours">Few Hours a Week</option>
          </Select>
        </FormGroup>

        <FormGroup>
          <Label>Skills (Can select multiple)</Label>
          <MultiSelect>
            {skills.map(skill => (
              <SkillTag
                key={skill}
                selected={selectedSkills.includes(skill)}
                onClick={() => handleSkillToggle(skill)}
              >
                {skill}
              </SkillTag>
            ))}
          </MultiSelect>
        </FormGroup>

        <FormGroup>
          <Label>Time commitment</Label>
          <Select
            value={formData.timeCommitment}
            onChange={(e) => handleInputChange('timeCommitment', e.target.value)}
          >
            <option value="">Select time commitment...</option>
            <option value="1-10">1-10 hours/week</option>
            <option value="10-40">10-40 hours/week</option>
            <option value="40+">40+ hours/week</option>
          </Select>
        </FormGroup>

        <FormGroup>
          <Label>Pay</Label>
          <Select
            value={formData.pay}
            onChange={(e) => handleInputChange('pay', e.target.value)}
          >
            <option value="">Select payment structure...</option>
            <option value="hourly">Hourly</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
            <option value="commission">Commission</option>
            <option value="partnership">Partnership</option>
          </Select>
        </FormGroup>

        <FormGroup>
          <Label>Work Structure</Label>
          <Select
            value={formData.remote}
            onChange={(e) => handleInputChange('remote', e.target.value)}
          >
            <option value="">Select work structure...</option>
            <option value="on-site">On-site</option>
            <option value="hybrid">Hybrid</option>
            <option value="remote">Remote</option>
          </Select>
        </FormGroup>

        <ButtonContainer>
          <FAQButton onClick={() => setShowFAQ(true)}>
            <FaQuestionCircle />
            FAQ
          </FAQButton>
          {isEditing && (
            <PostButton 
              style={{ background: '#6c757d' }} 
              onClick={() => navigate('/profile')}
              disabled={loading}
            >
              <FaTimes />
              CANCEL
            </PostButton>
          )}
          <PostButton onClick={handlePost} disabled={loading}>
            <FaEdit />
            {loading ? (isEditing ? 'UPDATING...' : 'SUBMITTING...') : (isEditing ? 'UPDATE' : 'POST')}
          </PostButton>
        </ButtonContainer>
      </FormCard>

      {showFAQ && (
        <Modal onClick={() => setShowFAQ(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <h2 style={{ color: '#8B5A96', marginBottom: '1.5rem' }}>Frequently Asked Questions</h2>
            
            {faqItems.map((item, index) => (
              <FAQItem key={index}>
                <FAQQuestion>{item.question}</FAQQuestion>
                <FAQAnswer>{item.answer}</FAQAnswer>
              </FAQItem>
            ))}
            
            <CloseButton onClick={() => setShowFAQ(false)}>
              Close
            </CloseButton>
          </ModalContent>
        </Modal>
      )}

      {showConfirmDialog && (
        <ConfirmDialog
          message={isEditing ? 'Are you sure you want to save these changes?' : 'Are you sure you are satisfied with the post?'}
          onConfirm={handleConfirm}
          onCancel={() => setShowConfirmDialog(false)}
          confirmText="OK"
          cancelText="Cancel"
          primaryColor="#8B5A96"
        />
      )}
    </Container>
  );
};

export default ShareHustle;
